import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, POLLING_INTERVALS, STALE_TIMES } from '@/lib/api'
import { sessionQueryKeys } from '@/lib/query/query-keys'
import { invalidateNotificationsDomain } from '@/lib/query/shell-cache'
import type {
  ApiNotification,
  ApiNotificationsListResponse,
  ApiNotificationsSummaryResponse,
} from '@/lib/api/types'

export interface NotificationListItemDto {
  id: string
  type: string
  title: string
  message: string
  data?: Record<string, unknown> | null
  href?: string
  opensKaizen?: boolean
  read: boolean
  createdAt: string
}

export interface NotificationsListDto {
  items: NotificationListItemDto[]
  total: number
  unreadCount: number
  pageSize: number
  nextCursor: string | null
  hasMore: boolean
}

export interface NotificationsSummaryDto {
  items: NotificationListItemDto[]
  unreadCount: number
  total: number
  previewLimit: number
  nextCursor?: string | null
}

interface UseNotificationsSummaryOptions {
  enabled?: boolean
  poll?: boolean
  initialData?: NotificationsSummaryDto
}

interface UseNotificationsListOptions {
  enabled?: boolean
  pageSize?: number
}

import { PAGINATION } from '@/lib/config/constants'

const DEFAULT_PREVIEW_LIMIT = PAGINATION.PREVIEW_LIMIT
const DEFAULT_NOTIFICATIONS_PAGE_SIZE = PAGINATION.DEFAULT_PAGE_SIZE


function normalizeNotificationType(type: string): string {
  const normalized = `${type ?? ''}`.trim().toLowerCase()
  if (normalized === 'mission_claimed' || normalized === 'mission-claimed') return 'mission-claim'
  if (normalized === 'mission_completed' || normalized === 'mission-completed') return 'mission-claim'
  if (normalized === 'achievement_unlocked' || normalized === 'achievement-unlocked') return 'achievement-unlocked'
  if (normalized === 'level_up' || normalized === 'profile_level_up') return 'level-up'
  return normalized.replace(/_/g, '-')
}

function resolveNotificationHref(notification: ApiNotification, normalizedType: string): string | undefined {
  if (notification.href && notification.href.trim().length > 0) {
    return notification.href
  }

  const missionId =
    notification.data && typeof notification.data === 'object' && typeof notification.data.missionId === 'string'
      ? notification.data.missionId
      : null

  if (
    normalizedType === 'mission-claim' ||
    normalizedType === 'mission-claimed' ||
    normalizedType === 'mission-completed'
  ) {
    return missionId ? `/missions?missionId=${encodeURIComponent(missionId)}` : '/missions'
  }

  if (normalizedType === 'achievement-unlocked') {
    return '/dashboard/achievements'
  }

  if (normalizedType === 'trial-started' || normalizedType === 'trial-ending') {
    return '/settings/subscription'
  }

  return undefined
}

function mapApiNotificationToListItemDto(notification: ApiNotification): NotificationListItemDto {
  const normalizedType = normalizeNotificationType(notification.type)
  return {
    id: notification.id,
    type: normalizedType,
    title: notification.title ?? 'Notificacao',
    message: notification.message ?? '',
    data: notification.data ?? null,
    href: resolveNotificationHref(notification, normalizedType),
    opensKaizen:
      notification.data && typeof notification.data === 'object' && notification.data.opensKaizen === true,
    read: notification.read ?? Boolean(notification.readAt),
    createdAt: notification.createdAt,
  }
}

export function mapApiNotificationsListToNotificationsListDto(
  payload: ApiNotificationsListResponse,
): NotificationsListDto {
  return {
    items: (payload.items ?? []).map(mapApiNotificationToListItemDto),
    total: Math.max(0, Number(payload.total ?? 0)),
    unreadCount: Math.max(0, Number(payload.unreadCount ?? 0)),
    pageSize: Math.max(1, Number(payload.pageSize ?? payload.items?.length ?? DEFAULT_NOTIFICATIONS_PAGE_SIZE)),
    nextCursor: typeof payload.nextCursor === 'string' && payload.nextCursor.trim().length > 0 ? payload.nextCursor : null,
    hasMore: Boolean(payload.hasMore),
  }
}

export function mapApiNotificationsSummaryToSummaryDto(
  payload: ApiNotificationsSummaryResponse,
): NotificationsSummaryDto {
  return {
    items: (payload.items ?? []).map(mapApiNotificationToListItemDto),
    unreadCount: Math.max(0, Number(payload.unreadCount ?? 0)),
    total: Math.max(0, Number(payload.total ?? payload.items?.length ?? 0)),
    previewLimit: Math.max(1, Number(payload.previewLimit ?? DEFAULT_PREVIEW_LIMIT)),
    nextCursor:
      typeof payload.nextCursor === 'string' && payload.nextCursor.trim().length > 0 ? payload.nextCursor : null,
  }
}

export function mapNotificationsListToSummaryDto(
  payload: Pick<NotificationsListDto, 'items' | 'total' | 'unreadCount'>,
  previewLimit = DEFAULT_PREVIEW_LIMIT,
): NotificationsSummaryDto {
  return {
    items: (payload.items ?? []).slice(0, previewLimit),
    unreadCount: Math.max(0, Number(payload.unreadCount ?? 0)),
    total: Math.max(0, Number(payload.total ?? 0)),
    previewLimit,
  }
}

function buildSummarySeedKey(summary: NotificationsSummaryDto | undefined, userId: string | null | undefined) {
  if (!summary || !userId) return null

  const itemsKey = summary.items
    .map((item) => `${item.id}:${item.read ? '1' : '0'}:${item.createdAt}`)
    .join('|')

  return `${userId}:${summary.unreadCount}:${summary.total}:${summary.previewLimit}:${itemsKey}`
}

function buildSummaryQueryKey(userId: string | null | undefined) {
  return sessionQueryKeys.notificationsSummary(userId)
}

function buildListQueryKey(userId: string | null | undefined, pageSize: number) {
  return sessionQueryKeys.notificationsList(userId, pageSize)
}

function buildNotificationsListEndpoint(pageSize: number, cursor?: string | null) {
  const params = new URLSearchParams()
  params.set('limit', String(pageSize))
  if (cursor) params.set('cursor', cursor)
  return `/notifications?${params.toString()}`
}

function invalidateNotificationQueries(queryClient: ReturnType<typeof useQueryClient>) {
  const summaryQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: ['session', 'notifications-summary'] })
    .map((query) => {
      const userId = typeof query.queryKey[2] === 'string' ? query.queryKey[2] : null
      return invalidateNotificationsDomain(queryClient, userId)
    })

  return Promise.all(summaryQueries)
}

export function useNotificationsSummary(
  userId: string | null | undefined,
  options: UseNotificationsSummaryOptions = {},
) {
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => buildSummaryQueryKey(userId), [userId])
  const seededInitialDataRef = useRef<string | null>(null)
  const initialDataSeedKey = useMemo(
    () => buildSummarySeedKey(options.initialData, userId),
    [options.initialData, userId],
  )

  useEffect(() => {
    if (!userId || !options.initialData) return
    if (seededInitialDataRef.current === initialDataSeedKey) return
    const existing = queryClient.getQueryData<NotificationsSummaryDto>(queryKey)
    if (existing) {
      seededInitialDataRef.current = initialDataSeedKey
      return
    }
    queryClient.setQueryData(queryKey, options.initialData)
    seededInitialDataRef.current = initialDataSeedKey
  }, [initialDataSeedKey, options.initialData, queryClient, queryKey, userId])

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const data = await api.get<ApiNotificationsSummaryResponse>('/notifications/summary')
      return mapApiNotificationsSummaryToSummaryDto(data)
    },
    enabled: Boolean(userId) && (options.enabled ?? true),
    refetchInterval: options.poll ? POLLING_INTERVALS.NOTIFICATIONS : false,
    staleTime: STALE_TIMES.NORMAL,
  })

  return {
    summary: query.data ?? options.initialData ?? mapNotificationsListToSummaryDto({ items: [], total: 0, unreadCount: 0 }),
    loading: query.isLoading,
    refetch: query.refetch,
  }
}

export function useNotificationsList(
  userId: string | null | undefined,
  options: UseNotificationsListOptions = {},
) {
  const pageSize = Math.max(1, Number(options.pageSize ?? DEFAULT_NOTIFICATIONS_PAGE_SIZE))

  const query = useInfiniteQuery({
    queryKey: buildListQueryKey(userId, pageSize),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const data = await api.get<ApiNotificationsListResponse>(
        buildNotificationsListEndpoint(pageSize, pageParam),
      )
      return mapApiNotificationsListToNotificationsListDto(data)
    },
    enabled: Boolean(userId) && (options.enabled ?? true),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor ?? undefined : undefined),
    staleTime: STALE_TIMES.NORMAL,
  })

  const pages = query.data?.pages ?? []
  const firstPage = pages[0]

  return {
    items: pages.flatMap((page) => page.items),
    total: firstPage?.total ?? 0,
    unreadCount: firstPage?.unreadCount ?? 0,
    pageSize: firstPage?.pageSize ?? pageSize,
    hasMore: firstPage?.hasMore ?? false,
    nextCursor: firstPage?.nextCursor ?? null,
    loading: query.isLoading,
    loadingMore: query.isFetchingNextPage,
    refreshing: query.isRefetching,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  }
}

export function useNotificationActions() {
  const queryClient = useQueryClient()

  const markReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await api.post(`/notifications/${notificationId}/read`)
    },
    onSuccess: async () => {
      await invalidateNotificationQueries(queryClient)
    },
  })

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await api.post('/notifications/read-all')
    },
    onSuccess: async () => {
      await invalidateNotificationQueries(queryClient)
    },
  })

  const markAsRead = useCallback(
    async (notificationId: string) => {
      await markReadMutation.mutateAsync(notificationId)
    },
    [markReadMutation],
  )

  const markAllAsRead = useCallback(async () => {
    await markAllReadMutation.mutateAsync()
  }, [markAllReadMutation])

  return {
    markAsRead,
    markAllAsRead,
    isMarkingRead: markReadMutation.isPending,
    isMarkingAllRead: markAllReadMutation.isPending,
  }
}
